import { motion } from 'framer-motion';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import GlowBackground from '@/components/site/GlowBackground';
import SectionHeader from '@/components/site/SectionHeader';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Clock, Fingerprint, Landmark, BookMarked, Scroll, FileText, MapPin } from 'lucide-react';

const topics = [
  {
    q: 'Can SAPS arrest without a warrant?',
    a: 'Yes — but only within the strict tests set out in section 40 of the Criminal Procedure Act 51 of 1977. In Minister of Safety and Security v Sekhoto 2011 (5) SA 367 (SCA) the Supreme Court of Appeal confirmed four jurisdictional facts that must be met: (i) the arrestor must be a peace officer; (ii) the officer must entertain a suspicion; (iii) that suspicion must be that the person committed a Schedule 1 offence; and (iv) the suspicion must rest on reasonable grounds. Even where those facts are present, the officer retains a discretion which must be exercised properly.',
    icon: Scroll,
  },
  {
    q: 'Can the police detain me overnight?',
    a: 'Detention is only lawful for as long as its legal basis subsists. Section 35(1)(d) of the Constitution requires that an arrested person be brought before a court as soon as reasonably possible — and no later than 48 hours after arrest (subject to weekends and public holidays). Detention beyond justified grounds may itself be unlawful, as recognised in Zealand v Minister of Justice.',
    icon: Clock,
  },
  {
    q: 'What are my constitutional rights after arrest?',
    a: 'Section 35 of the Constitution guarantees the right to remain silent, to be informed promptly of the reason for the arrest, to consult with a legal practitioner (and to have one assigned where substantial injustice would otherwise result), to be brought before a court within 48 hours, and to conditions of detention consistent with human dignity.',
    icon: ShieldCheck,
  },
  {
    q: 'When must I appear in court?',
    a: 'As soon as reasonably possible and not later than 48 hours after arrest — extended to the first court day if the 48-hour period would expire outside ordinary court hours (section 50 of the CPA).',
    icon: Landmark,
  },
  {
    q: 'What if the charges were withdrawn?',
    a: 'The withdrawal of criminal charges is a relevant factor but not conclusive of unlawfulness. The civil enquiry asks whether the arrest and any continued detention were themselves lawful under the Constitution and the CPA — not whether the prosecution ultimately proceeded.',
    icon: FileText,
  },
  {
    q: 'Can you sue the Minister of Police?',
    a: 'Civil claims arising from allegedly unlawful arrest and detention by SAPS members are typically brought against the Minister of Police in his official capacity. Notice provisions under the Institution of Legal Proceedings Against Certain Organs of State Act 40 of 2002 apply.',
    icon: BookMarked,
  },
  {
    q: 'How long do I have to institute a claim?',
    a: 'Notice must be given within six months of the debt arising, in terms of the Institution of Legal Proceedings Against Certain Organs of State Act 40 of 2002, and the claim itself is generally subject to a three-year prescription period under the Prescription Act 68 of 1969. Deadlines are strict — do not delay.',
    icon: Clock,
  },
  {
    q: 'Is the burden on me to prove the arrest was unlawful?',
    a: 'No. Once you show that the arrest and/or detention took place, the burden shifts to the police to justify the deprivation of liberty. This principle was reinforced in Zealand v Minister of Justice and Constitutional Development.',
    icon: Fingerprint,
  },
];

export default function KnowYourRights() {
  return (
    <div className="relative pt-28 md:pt-36 pb-16" data-testid="rights-page">
      <div className="absolute inset-0 pointer-events-none"><GlowBackground /></div>
      <div className="relative mx-auto max-w-5xl px-5 md:px-8">
        <SectionHeader
          eyebrow="Educational · Not legal advice"
          title={<>Know your <span className="text-[#FFD400]">rights.</span></>}
          subtitle="A plain-English guide to unlawful arrest and detention in South Africa, grounded in the Constitution, the Criminal Procedure Act and leading authorities."
        />

        <div className="mt-16 border-t border-white/10">
          <Accordion type="single" collapsible className="w-full" defaultValue="item-0">
            {topics.map((t, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-white/10">
                <AccordionTrigger data-testid={`rights-q-${i}`} className="py-6 md:py-7 text-left [&_svg]:hidden">
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="flex items-center gap-4 w-full"
                  >
                    <span className="hidden md:inline-flex h-10 w-10 rounded-full border border-white/10 items-center justify-center flex-shrink-0">
                      <t.icon className="h-4 w-4 text-[#39A8FF]" strokeWidth={1.6} />
                    </span>
                    <span className="font-poppins font-semibold text-white text-lg md:text-2xl leading-snug tracking-tight">{t.q}</span>
                    <span className="ml-auto text-[#39A8FF] text-2xl leading-none">+</span>
                  </motion.div>
                </AccordionTrigger>
                <AccordionContent data-testid={`rights-a-${i}`} className="pb-8">
                  <p className="max-w-3xl font-inter text-white/70 text-base md:text-lg leading-relaxed">{t.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="mt-16 rounded-3xl border border-white/10 bg-[#0d1826]/70 p-8 md:p-10">
          <div className="text-[11px] font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400]">Next Step</div>
          <h3 className="mt-4 font-poppins font-bold text-white text-3xl md:text-4xl leading-tight">Ready to check if you have a claim?</h3>
          <p className="mt-4 max-w-2xl font-inter text-white/70">Take the free 3-minute assessment. A South African attorney will review your responses.</p>
          <Link to="/calculator" data-testid="rights-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#FFD400] px-7 py-4 text-sm font-poppins font-bold uppercase tracking-wide text-[#07111F]">
            Start Free Assessment <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-10 flex items-center gap-3 text-xs font-inter text-white/40">
          <MapPin className="h-4 w-4" /> Serving clients across all nine provinces of South Africa.
        </div>
      </div>
    </div>
  );
}
